import { SelectOption } from '../purchase-order-detail/sections/section-base.types';
import { PHONE_FIELD_ERROR, PHONE_FIELD_MESSAGE } from '../phone-field/phone-field.constants';

// Field types the dynamic company form can render. The backend sends them UPPER_CASE
// (STRING / SELECT / NUMBER); the JSON config uses the lowercase form. The factory normalizes
// everything to these codes so the renderer only ever switches on one vocabulary.
export const FH_FIELD_TYPE = {
  SELECT:   'select',
  STRING:   'string',
  NUMBER:   'number',
  TEXTAREA: 'textarea',
  BOOLEAN:  'boolean',
  FILE:     'file',
} as const;
export type FhFieldType = typeof FH_FIELD_TYPE[keyof typeof FH_FIELD_TYPE];

// Maps the backend's UPPER_CASE `fields[].type` to the internal codes above.
export const FH_BACKEND_TYPE_MAP: Record<string, FhFieldType> = {
  STRING:   FH_FIELD_TYPE.STRING,
  SELECT:   FH_FIELD_TYPE.SELECT,
  NUMBER:   FH_FIELD_TYPE.NUMBER,
  TEXTAREA: FH_FIELD_TYPE.TEXTAREA,
  BOOLEAN:  FH_FIELD_TYPE.BOOLEAN,
  FILE:     FH_FIELD_TYPE.FILE,
};

// Datasource tokens declared in the JSON config under `metadata.service.type`. Each one maps to a
// concrete call in MakersFinancesMgrService (see CompanyFieldSourceFactory).
export const FH_SERVICE_TYPE = {
  CONTACT:         'contact_service',
  OWNER_SEARCH:    'owner_search_service',
  COMPANY_GROUP:   'company_group_service',
  TYPE_OF_COMPANY: 'type_of_company_service',
  COUNTRIES:       'countries_service',
} as const;
export type FhServiceType = typeof FH_SERVICE_TYPE[keyof typeof FH_SERVICE_TYPE];

// How a section is rendered: a normal grid of fields, a multi-file dropzone card, or the
// repeatable Agency Rebates rows.
export const FH_SECTION_KIND = {
  FIELDS:  'fields',
  FILES:   'files',
  REBATES: 'rebates',
} as const;
export type FhSectionKind = typeof FH_SECTION_KIND[keyof typeof FH_SECTION_KIND];

// `metadata.validation` values — drive both the <one-input type> and the extra validator.
export const FH_FIELD_VALIDATION = {
  EMAIL: 'email',
  URL:   'url',
} as const;
export type FhFieldValidation = typeof FH_FIELD_VALIDATION[keyof typeof FH_FIELD_VALIDATION];

// `metadata.value_payload` — target field name where the selected option's code should be sent.
// When set, the label is sent to the original `payload` field and the code is sent to this field.
// Example: tax_regime (label) + tax_regime_value (code).
// Declared here for documentation; the type is just `string | null`.
export type FhValuePayload = string;

// The JSON encodes `required` / `multiple` as strings, not booleans. "conditional" means the
// field is only required through `required_when`.
export const FH_FLAG = {
  TRUE:        'true',
  FALSE:       'false',
  CONDITIONAL: 'conditional',
} as const;

// Yes/No select values used by the boolean-ish selects (international_company, is_it_a_person...).
export const FH_YES_NO = {
  YES: 'yes',
  NO:  'no',
} as const;

// Options synthesized for any field whose resolved type is BOOLEAN (see
// CompanyFieldFactory.resolveOptions) — the JSON declares no options of its own for these. Ids are
// the stringified booleans (FH_FLAG.TRUE/FALSE); company-form.rules.ts and
// company-form.payload.ts both coerce them back to a real true/false.
export const FH_BOOLEAN_OPTIONS: SelectOption[] = [
  { id: FH_FLAG.TRUE, name: 'Yes' },
  { id: FH_FLAG.FALSE, name: 'No' },
];

// The only non-field predicate key: `visible_when: { countries: ['CO','MX'] }`.
export const FH_PREDICATE_COUNTRIES_KEY = 'countries';

// Safety net for a stale config: earlier revisions keyed predicates on `company_type` while the
// field id is `type_of_company`. The asset has been normalized, but the engine still resolves
// aliases so an un-migrated config degrades to the right behaviour instead of matching nothing.
export const FH_PREDICATE_KEY_ALIASES: Record<string, string> = {
  company_type: 'type_of_company',
};

// Role tokens used by `metadata.visible_for` / `metadata.editable_by`.
export const FH_ROLE = {
  BBR_ADMIN:        'bbr_admin',
  FINANCE_APPROVER: 'finance_approver',
} as const;

// Well-known field ids the form logic reacts to by name.
export const FH_FIELD = {
  INTERNATIONAL_COMPANY: 'international_company',
  TYPE_OF_COMPANY:       'type_of_company',
  COMPANY_GROUP:         'company_group',
  TEST_COMPANY:          'test_company',
  LEGAL_POC:             'legal_poc_id',
  FINANCIAL_POC:         'financial_poc_id',
  AGENCY_REBATES:        'agency_rebates',
  DOCUMENTS:             'documents',
  REBATE_ID:             'id',
  REBATE_CURRENCY:       'rebate_currency',
  REBATE_THRESHOLD:      'rebate_threshold',
  REBATE_PERCENTAGE:     'rebate_percentage',
  TAX_ID:                'tax_id',
  PROOF_OF_EXECUTION_REQUIREMENTS: 'proof_of_execution_requirements',
} as const;

// Fields that become read-only for every role once the company has an integration_settlement_id
// (it was sent to settlement): Tax ID and "Is it an International Company?".
export const FH_SETTLEMENT_LOCKED_FIELDS: ReadonlyArray<string> = [
  FH_FIELD.TAX_ID,
  FH_FIELD.INTERNATIONAL_COMPANY,
];

// "Enable for Trato" header switch. It is not a config field: the form owns this control (outside
// every section) and the payload builder adds `trato_enabled` to the body. Only the finance
// approver can change it; everyone else sees it disabled. Default ON.
export const FH_TRATO = {
  CONTROL: 'trato_enabled',
  PAYLOAD: 'trato_enabled',
} as const;

// Payload keys that are not derived from a rendered field.
// COUNTRY matches the real POST/PUT /companies request body (`country`, confirmed against
// docs/swagger.json in makers-finances-mgr).
// AGENCY_REBATE is the inline array both POST and PUT /companies/{id} accept (see
// docs/PRD/agency-rebates-put-company.md) — the client sends the full desired state (even `[]`)
// and the backend reconciles: ids missing from the array are soft-deleted, items without an `id`
// are created, items with an `id` are updated. A hidden Rebates section omits this key entirely
// (see company-form.payload.ts) so an existing company's rebates are left untouched whenever the
// company type isn't Agency.
export const FH_PAYLOAD = {
  COUNTRY: 'country',
  AGENCY_REBATE: 'agency_rebate',
  // Numeric catalog id of the selected company type, sent alongside the `type_of_company` code.
  // The type select's control value is the snake_case `code`; the backend also wants the catalog
  // row id (CatalogItem.id from GET /company-type-options), which the form captures separately
  // since fh-select only ever holds the code (see CompanyFormComponent.companyTypeIdByCode).
  TYPE_OF_COMPANY_ID: 'type_of_company_id',
} as const;

// Section label the factory keys off to resolve FH_SECTION_KIND.REBATES.
export const FH_SECTION_LABEL = {
  AGENCY_REBATES: 'Agency Rebates',
} as const;

// Validator error keys. Prefixed so they never collide with Angular's built-ins and can be fed to
// the appFormErrors directive's customFormatErrors.
export const FH_FIELD_ERROR = {
  PATTERN:             'fhPattern',
  EMAIL:               'fhEmail',
  URL:                 'fhUrl',
  DECIMALS:            'fhDecimals',
  DUPLICATE_THRESHOLD: 'fhDuplicateThreshold',
} as const;

export const FH_ERROR_MESSAGE: Record<string, string> = {
  required:                             '*Required',
  [FH_FIELD_ERROR.PATTERN]:             'Invalid format.',
  [FH_FIELD_ERROR.EMAIL]:               'Enter a valid email address.',
  [FH_FIELD_ERROR.URL]:                 'Enter a valid URL.',
  [FH_FIELD_ERROR.DECIMALS]:            'Too many decimal places.',
  [FH_FIELD_ERROR.DUPLICATE_THRESHOLD]: 'Rebate thresholds must be unique.',
  [PHONE_FIELD_ERROR.LENGTH]:           PHONE_FIELD_MESSAGE[PHONE_FIELD_ERROR.LENGTH],
  min:                                  'Value is below the minimum allowed.',
  max:                                  'Value is above the maximum allowed.',
};

// Per-country Tax ID format reference (PRD HU4 Tabla D.1 "Formato de Tax ID por país"). The
// backend regex (D.1 "Regex del servicio") stays the source of truth for whether a value passes —
// it is permissive and only checks the character class. When a tax_id FAILS that regex, the
// generic "Invalid format." tells the user nothing, so these entries drive a country-specific
// inline message that names the document type and shows an example of the shape to enter.
//
// Keyed by the session country code (upper-case). The international case is not a country: a
// company with `Is International Company? = Yes` takes the fixed `IDE` type (PRD B.7), so it is
// keyed by FH_TAX_ID_FORMAT_INTERNATIONAL instead of the session country.
export const FH_TAX_ID_FORMAT_INTERNATIONAL = 'INTERNATIONAL';

export const FH_TAX_ID_FORMAT: Record<string, { type: string; example: string }> = {
  CO: { type: 'NIT',  example: '123456789-6' },
  MX: { type: 'RFC',  example: 'ABC850913H71' },
  BR: { type: 'CNPJ', example: '00.000.000/0000-00' },
  AR: { type: 'CUIT', example: '20123456789' },
  CL: { type: 'RUT',  example: '12345678-9' },
  UY: { type: 'RUT',  example: '123456789012' },
  PE: { type: 'RUC',  example: '12345678901' },
  EC: { type: 'RUC',  example: '1234567890001' },
  CR: { type: 'ID',   example: '3101123456' },
  [FH_TAX_ID_FORMAT_INTERNATIONAL]: { type: 'IDE', example: '4 to 30 characters' },
};

// Builds the inline Tax ID format error (PRD "Copys": "Invalid {tax id type}. Expected format:
// {ejemplo}"). Returns null when the country has no known format so the caller falls back to the
// generic FH_FIELD_ERROR.PATTERN message rather than inventing one.
export function fhTaxIdFormatMessage(country: string, isInternational: boolean): string | null {
  const key = isInternational
    ? FH_TAX_ID_FORMAT_INTERNATIONAL
    : String(country ?? '').trim().toUpperCase();
  const format = FH_TAX_ID_FORMAT[key];
  return format ? `Invalid ${format.type}. Expected format: ${format.example}` : null;
}

// User-facing copy. financial-hub does not use ngx-translate — labels live in constants
// (see CLAUDE.md) or come from the CMS config with a constant fallback.
export const FH_COMPANY_LABEL = {
  PAGE_TITLE:         'New company',
  EDIT_PAGE_TITLE:    'Edit company',
  REQUIRED_LEGEND:    'Required Information *',
  TEST_SWITCH:        'Company test',
  TRATO_SWITCH:       'Enable for Trato',
  SAVE_DRAFT:         'Save draft',
  SAVING:             'Saving...',
  SUBMIT:             'Submit for verification',
  SAVE_CHANGES:       'Save changes',
  CANCEL:             'Cancel',
  DROPZONE:           'Drag and drop files here or',
  BROWSE:             'Browse file',
  REMOVE:             'Remove',
  PREVIEW:            'Preview',
  UPLOADING:          'Uploading...',
  ADD_REBATE:         'Add rebate',
  EDIT_CONTACT:       'Edit contact',
} as const;

// Fallbacks for the file cards when the config omits `accept` / `max_size_mb`.
export const FH_FILE_FALLBACK = {
  ACCEPT:      ['pdf', 'jpg', 'png'] as string[],
  MAX_SIZE_MB: 25,
} as const;

// Extension to mime, used to build the <input accept> attribute and to validate dropped files.
export const FH_FILE_MIME_MAP: Record<string, string[]> = {
  pdf:  ['application/pdf'],
  jpg:  ['image/jpeg'],
  jpeg: ['image/jpeg'],
  png:  ['image/png'],
};

// field_name/min/max enforcement per docs/documents/COMPANY_DOCUMENTS_API.md in makers-finances-mgr.
export const FH_FILE_ERROR = {
  INVALID_FORMAT:   'invalid_format',
  SIZE_EXCEEDED:    'size_exceeded',
  UPLOAD_FAILED:    'upload_failed',
  LOAD_FAILED:      'load_failed',
  ADD_FAILED:       'add_failed',
  REMOVE_FAILED:    'remove_failed',
  MAX_EXCEEDED:     'max_exceeded',
  MIN_VIOLATION:    'min_violation',
  TYPE_NOT_ALLOWED: 'type_not_allowed',
} as const;
export type FhFileError = typeof FH_FILE_ERROR[keyof typeof FH_FILE_ERROR] | null;

export const FH_FILE_MESSAGE: Record<string, string> = {
  [FH_FILE_ERROR.INVALID_FORMAT]:   'File type not allowed.',
  [FH_FILE_ERROR.SIZE_EXCEEDED]:    'File exceeds the maximum allowed size.',
  [FH_FILE_ERROR.UPLOAD_FAILED]:    'Error uploading the document. Please try again.',
  [FH_FILE_ERROR.LOAD_FAILED]:      'Could not load the existing documents. Please reload the page.',
  [FH_FILE_ERROR.ADD_FAILED]:       'Could not add the document. Please try again.',
  [FH_FILE_ERROR.REMOVE_FAILED]:    'Could not remove the document. Please try again.',
  [FH_FILE_ERROR.MAX_EXCEEDED]:     'Adding these documents would exceed the maximum allowed.',
  [FH_FILE_ERROR.MIN_VIOLATION]:    'Removing this document would drop below the minimum required.',
  [FH_FILE_ERROR.TYPE_NOT_ALLOWED]: 'This document type is not enabled for your country.',
  UPLOAD_COMPLETE:                  'Document uploaded successfully.',
  DOCUMENT_REMOVED:                 'Document removed.',
};

export const FH_REBATE = {
  DEFAULT_CURRENCY:  'USD',
  DUPLICATE_MESSAGE: 'This threshold is already used in another row.',
  LOAD_FAILED:       'Could not load this company\'s existing Agency Rebates. Please reload the page.',
} as const;

// First element matching this (in document order) is what a blocked submit scrolls to: a field in
// error, the files dropzone, a duplicated rebate row, or a rebate/files "*Required" label.
export const FH_INVALID_ELEMENT_SELECTOR = [
  '.cf-field--error',
  '.cf-rebate-row--error',
  '.cf-rebate-row .cf-field__error',
  '.fc-dropzone--error',
  '.fc-field__error',
].join(', ');

export const FH_COMPANY_MESSAGE = {
  CONFIG_FAILED: 'Could not load the company field configuration. Some fields may be missing.',
  LOAD_FAILED:   'Could not load the company. Please try again.',
  SAVE_DRAFT_OK: 'Draft saved.',
  SUBMIT_OK:     'Company submitted for approval.',
  UPDATE_OK:     'Company updated successfully.',
  SAVE_FAILED:   'Could not save the company. Please try again.',
} as const;

// Warnings emitted once when the two configs disagree - surfaced in the console, never to the user.
export const FH_WARN = {
  UNKNOWN_BACKEND_FIELD: '[company-form] backend returned a field with no entry in companies-form.config.json:',
  DUPLICATE_CONTROL:     '[company-form] duplicate field key, deduped with its payload:',
  UNKNOWN_PREDICATE_KEY: '[company-form] visible_when/required_when references an unknown field:',
  UNKNOWN_FIELD_TYPE:    '[company-form] unknown field type, rendering as text:',
  INVALID_REGEX:         '[company-form] invalid regex in field config, validator skipped:',
} as const;

export const FH_CONFIG_URL = {
  COMPANIES_FORM: 'assets/financial-hub/companies-form.config.json',
} as const;

// Custom component identifiers declared in the JSON config under `metadata.component`. Each value
// maps to a dedicated Angular component that replaces the default field renderer.
export const FH_COMPONENT = {
  PHONE:        'phone',
  SELECT_OTHER: 'select_other',
} as const;

// `metadata.component: "select_other"` fields (see SelectOtherFieldComponent). The option whose id
// equals OPTION_ID reveals the free-text input, and the typed text — never the literal option id —
// is what the control holds and the payload sends: the backend column is free TEXT and must never
// store the literal "Otro" (INI-13125-4).
export const FH_SELECT_OTHER = {
  OPTION_ID:   'Otro',
  PLACEHOLDER: 'Describe the process',
} as const;

// one-select keys - matches SelectOption {id,name}. Declared here so every select in the dynamic
// form uses the same contract.
export const FH_SELECT_KEYS = {
  id:              'id',
  name:            'name',
  displayProperty: 'name',
};
